import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { planHasFeature, roleCanSee, type AccountRole, type Feature } from '@dca/shared';

import { api, getToken, setToken, type Me } from './api.ts';

interface AuthState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    role: AccountRole,
    discordHandle?: string,
  ) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  can: (feature: Feature) => boolean;
  /** Whether this account type has the tab at all - role first, tier second. */
  sees: (tab: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await api.auth.me());
    } catch {
      // An expired or revoked token should log the session out rather than
      // leaving the app in a half-authenticated state.
      setToken(null);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      const { token } = await api.auth.login(email, password);
      setToken(token);
      await refresh();
    },
    [refresh],
  );

  const register = useCallback(
    async (email: string, password: string, role: AccountRole, discordHandle?: string) => {
      setError(null);
      const { token } = await api.auth.register(email, password, role, discordHandle);
      setToken(token);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(() => {
    setToken(null);
    setMe(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      error,
      login,
      register,
      logout,
      refresh,
      can: (feature) => (me ? planHasFeature(me.subscriber.tier, feature) : false),
      sees: (tab) => (me ? roleCanSee(me.subscriber.role, tab) : false),
    }),
    [me, loading, error, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** Small data-loading hook: enough for this app, no query library needed. */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
): { data: T | null; error: unknown; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
